import { useEventCallback } from '@gitbook/util-react-hooks';

import { useCurrentOrganizationId } from '../../state/organization';
import {
    generateConversationLocalId,
    provisionOrganizationConversation,
    useSetAgentPendingPrompt,
} from '../Agent';
import { useAgentDock } from './state';

/**
 * Start an agent conversation in the dock with a prompt already in its composer.
 *
 * This is how surfaces outside the dock ask the agent for something — the editor's selection menu,
 * an empty block, the page header. They supply the prompt alone: what the agent needs to know about
 * where the user is, the page and any selection included, is attached by the dock's own location
 * context, exactly as for a conversation started by hand.
 *
 * An unused draft is reused rather than replaced, so asking twice does not strand a conversation
 * nobody wrote in. Returns null outside an organization, where there is no agent to talk to.
 */
export function useStartAgentConversation(): ((input: { prompt?: string }) => void) | null {
    const organizationId = useCurrentOrganizationId();
    const { draftConversationId, startDraft } = useAgentDock(organizationId ?? '');
    const setPendingPrompt = useSetAgentPendingPrompt();

    const start = useEventCallback((input: { prompt?: string }) => {
        if (!organizationId) {
            return;
        }

        const conversationId = draftConversationId ?? generateConversationLocalId();
        if (!draftConversationId) {
            // Provisioned outside the state update: React may run an updater during a render it
            // then discards, which would abort the request and leave the agent with no token.
            void provisionOrganizationConversation({ organizationId, conversationId });
        }

        if (input.prompt) {
            setPendingPrompt({ conversationId, prompt: input.prompt });
        }
        startDraft(conversationId);
    });

    return organizationId ? start : null;
}
